import { apiGet, apiPost, apiPut } from './client';
import { Ticket, TicketListResponse, InsightsResponse, TrendsResponse } from '../types/ticket';

export interface ListTicketsParams {
  status?: string;
  assigneeId?: string;
  severity?: string;
  sortBy?: string;
  sortOrder?: string;
  maxResults?: string;
  page?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function listTickets(params?: ListTicketsParams): Promise<TicketListResponse> {
  return apiGet<TicketListResponse>('/tickets', params as Record<string, string>);
}

export function getTicket(ticketId: string): Promise<{ ticket: Ticket }> {
  return apiGet<{ ticket: Ticket }>(`/tickets/${ticketId}`);
}

export function searchTickets(
  q: string,
  opts?: { sortBy?: string; sortOrder?: string; maxResults?: string; page?: string; dateFrom?: string; dateTo?: string },
): Promise<TicketListResponse> {
  return apiGet<TicketListResponse>('/tickets/search', { q, ...opts });
}

export function getFilters(): Promise<{ filters: any; lastModified: string }> {
  return apiGet<{ filters: any; lastModified: string }>('/settings/filters');
}

export function updateFilters(filters: any): Promise<{ message: string }> {
  return apiPut<{ message: string }>('/settings/filters', filters);
}

export function triggerSync(fullSync?: boolean): Promise<{ message: string }> {
  return apiPost<{ message: string }>('/tickets/sync', fullSync ? { fullSync: true } : undefined);
}

export async function getLastSyncTime(): Promise<string | null> {
  const res = await listTickets({ sortBy: 'processedAt', sortOrder: 'desc', maxResults: '1' });
  return res.tickets[0]?.processedAt ?? null;
}

export interface AIConfigExample {
  title: string;
  description: string;
  comment_summary: string;
  summary: string;
}

export interface AIConfig {
  model: string;
  system_prompt?: string;
  user_prompt?: string;
  prompt?: string;
  examples?: AIConfigExample[];
}

export function getAIConfig(): Promise<{ config: AIConfig; lastModified: string }> {
  return apiGet('/settings/ai-config');
}

export function updateAIConfig(config: AIConfig): Promise<{ message: string }> {
  return apiPut('/settings/ai-config', config);
}

export function resummarizeTicket(ticketId: string): Promise<{ message: string }> {
  return apiPost(`/tickets/${ticketId}/resummarize`);
}

export function batchResummarize(ticketIds: string[]): Promise<{ message: string }> {
  return apiPost('/tickets/resummarize', { ticketIds });
}

export function getQueueStatus(): Promise<{
  queue: { messagesAvailable: number; messagesInFlight: number };
  dlq: { messagesAvailable: number };
}> {
  return apiGet('/settings/queue-status');
}

export function getInsights(): Promise<InsightsResponse> {
  return apiGet<InsightsResponse>('/insights');
}

export function refreshInsights(): Promise<{ message: string }> {
  return apiPost<{ message: string }>('/insights/refresh');
}

export function getTrends(severities?: string[]): Promise<TrendsResponse> {
  const params: Record<string, string> = {};
  if (severities?.length) params.severities = severities.join(',');
  return apiGet<TrendsResponse>('/insights/trends', params);
}

export function getWeeklyStatusReport(): Promise<import('../types/ticket').WeeklyStatusReport> {
  return apiGet('/reports/weekly-status');
}

export function getMyTicketsReport(assignee: string): Promise<import('../types/ticket').MyTicketsReport> {
  return apiGet('/reports/my-tickets', { assignee });
}

export function getSLAComplianceReport(): Promise<import('../types/ticket').SLAComplianceReport> {
  return apiGet('/reports/sla-compliance');
}

export function getRecurringIssuesReport(weeks: number): Promise<import('../types/ticket').RecurringIssuesReport> {
  return apiGet('/reports/recurring-issues', { weeks: String(weeks) });
}

// ---- On-demand Root Cause Themes (async job) ----

export interface StartThemeJobParams {
  assignedGroup?: string;
  dateFrom: string;
  dateTo: string;
  severities?: string[];
}

export function startThemeJob(
  params: StartThemeJobParams,
): Promise<import('../types/ticket').StartThemeJobResponse> {
  return apiPost('/themes', params);
}

export function getThemeJob(jobId: string): Promise<import('../types/ticket').ThemeJob> {
  return apiGet(`/themes/${jobId}`);
}

export function listThemeJobs(): Promise<{ jobs: import('../types/ticket').ThemeJobSummary[] }> {
  return apiGet('/themes');
}

export function cancelThemeJob(
  jobId: string,
): Promise<{ jobId: string; status: import('../types/ticket').ThemeJobStatus }> {
  return apiPost(`/themes/${jobId}/cancel`, {});
}

export interface CoverageResponse {
  total: number;
  tagged: number;
  coveragePct: number;
}

export function getResolverGroupCoverage(): Promise<CoverageResponse> {
  return apiGet<CoverageResponse>('/insights/coverage');
}

// ---- Scoped operations (partial sync + scoped re-summarize) ----

export interface TicketScope {
  timeBasis: 'created' | 'updated';
  dateFrom?: string;
  dateTo?: string;
  assignedGroups?: string[];
  severities?: string[];
  statuses?: string[];
  tags?: string[];
}

export interface LastOperation {
  type: string;
  scope: string;
  count: number;
  at: string;
}

/** Re-summarize a scoped subset with the current AI config (no Tickety re-fetch). */
export function scopedResummarize(scope: TicketScope): Promise<{ message: string; count: number }> {
  return apiPost('/tickets/resummarize', { filters: scope });
}

/** Partial sync: re-fetch a scoped subset from Tickety. */
export function triggerScopedSync(scope: TicketScope): Promise<{ message: string }> {
  return apiPost('/tickets/sync', scope);
}

export function getLastOperation(): Promise<{ lastOperation: LastOperation | null }> {
  return apiGet('/settings/last-sync');
}

/**
 * The signed-in user's alias, resolved server-side from the verified Midway
 * token. `alias` is null only if the request somehow reached the API without an
 * authorizer context, in which case the UI hides the identity chip.
 */
export function getMe(): Promise<{ alias: string | null }> {
  return apiGet('/me');
}
